/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { PaginatedProviderListList } from '../models/PaginatedProviderListList';
import type { PatchedScheduleTypeUpdate } from '../models/PatchedScheduleTypeUpdate';
import type { ProfessionalServicesAgreement } from '../models/ProfessionalServicesAgreement';
import type { ProviderDetail } from '../models/ProviderDetail';
import type { ProviderList } from '../models/ProviderList';
import type { ProviderScheduleBulkUpdate } from '../models/ProviderScheduleBulkUpdate';
import type { ProviderScheduleDatesResponse } from '../models/ProviderScheduleDatesResponse';
import type { ScheduleTypeUpdate } from '../models/ScheduleTypeUpdate';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ProvidersService {

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns PaginatedProviderListList
     * @throws ApiError
     */
    public static providersList({
        limit,
        offset,
        ordering,
        profileDisposition,
        registrationStatus,
        search,
        sourceType,
        specialties,
        status,
        statusCategory,
        statusKind,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * Profile Disposition
         *
         * * `NOT_STARTED` - Not Started
         * * `IN_PROGRESS` - In Progress
         * * `1099_COMPLETE` - 1099 Complete
         * * `MKTPLC_COMPLETE` - Marketplace Complete
         * * `REMOVED` - Removed
         */
        profileDisposition?: Array<'1099_COMPLETE' | 'IN_PROGRESS' | 'MKTPLC_COMPLETE' | 'NOT_STARTED' | 'REMOVED' | null>,
        /**
         * Registration Status
         *
         * * `SETUP` - Setup
         * * `MISSING_INFO` - Missing Info
         * * `COMPLETE` - Complete
         * * `REJECTED` - Rejected
         */
        registrationStatus?: Array<'COMPLETE' | 'MISSING_INFO' | 'REJECTED' | 'SETUP'>,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Source Type
         *
         * * `ORGANIZATION` - Organization
         * * `MARKETPLACE` - Marketplace
         */
        sourceType?: Array<'MARKETPLACE' | 'ORGANIZATION' | null>,
        /**
         * Specialties
         *
         * * `ANESTHESIA` - Anesthesiology
         * * `CARDIOVASCULAR_DISEASE` - Cardiovascular Disease
         * * `COLON_AND_RECTAL_SURGERY` - Colon & Rectal Surgery
         * * `CRITICAL_CARE` - Critical Care Medicine
         * * `DERMATOLOGY` - Dermatology
         * * `EMERGENCY_MEDICINE` - Emergency Medicine
         * * `PRIMARY_CARE` - Family Medicine
         * * `GASTROENTEROLOGY` - Gastroenterology
         * * `GENERAL_PRACTICE` - General Practice
         * * `HOSPITAL_MEDICINE` - Hospital Medicine
         * * `INFECTIOUS_DISEASE` - Infectious Disease
         * * `INTERNAL_MEDICINE` - Internal Medicine
         * * `INTERVENTIONAL_CARDIOLOGY` - Interventional Cardiology
         * * `MEDICAL_ONCOLOGY` - Medical Oncology
         * * `NEPHROLOGY` - Nephrology
         * * `NEUROCRITICAL_CARE` - Neurocritical Care
         * * `NEUROLOGICAL_SURGERY` - Neurological Surgery
         * * `NEUROLOGY` - Neurology
         * * `OBSTETRICS_AND_GYNECOLOGY` - Obstetrics & Gynecology
         * * `OPHTHALMOLOGY` - Ophthalmology
         * * `ORAL_AND_MAXILLOFACIAL_SURGERY` - Oral & Maxillofacial Surgery
         * * `ORTHOPAEDIC_SURGERY` - Orthopaedic Surgery
         * * `OTOLARYNGOLOGY` - Otolaryngology
         * * `PAIN_MEDICINE` - Pain Medicine
         * * `PATHOLOGY` - Pathology
         * * `PEDIATRICS` - Pediatrics
         * * `PEDIATRIC_ANESTHESIA` - Pediatric Anesthesia
         * * `PEDIATRIC_CRITICAL_CARE` - Pediatric Critical Care
         * * `PEDIATRIC_EMERGENCY_MEDICINE` - Pediatric Emergency Medicine
         * * `PEDIATRIC_INFECTIOUS_DISEASES` - Pediatric Infectious Diseases
         * * `PHYSICAL_MEDICINE_AND_REHABILITATION` - Physical Medicine & Rehabilitation
         * * `PLASTIC_SURGERY` - Plastic Surgery
         * * `PREVENTIVE_MEDICINE` - Preventive Medicine
         * * `PULMONOLOGY` - Pulmonary Disease
         * * `PSYCHIATRY` - Psychiatry
         * * `RADIOLOGY` - Radiology
         * * `SURGERY` - Surgery
         * * `THORACIC_SURGERY` - Thoracic Surgery (Cardiothoracic Vascular Surgery)
         * * `TRANSPLANT_SURGERY` - Transplant Surgery
         * * `UROLOGY` - Urology
         * * `INTERVENTIONAL_RADIOLOGY` - Vascular & Interventional Radiology
         * * `OTHER` - Other
         */
        specialties?: Array<'ANESTHESIA' | 'CARDIOVASCULAR_DISEASE' | 'COLON_AND_RECTAL_SURGERY' | 'CRITICAL_CARE' | 'DERMATOLOGY' | 'EMERGENCY_MEDICINE' | 'GASTROENTEROLOGY' | 'GENERAL_PRACTICE' | 'HOSPITAL_MEDICINE' | 'INFECTIOUS_DISEASE' | 'INTERNAL_MEDICINE' | 'INTERVENTIONAL_CARDIOLOGY' | 'INTERVENTIONAL_RADIOLOGY' | 'MEDICAL_ONCOLOGY' | 'NEPHROLOGY' | 'NEUROCRITICAL_CARE' | 'NEUROLOGICAL_SURGERY' | 'NEUROLOGY' | 'OBSTETRICS_AND_GYNECOLOGY' | 'OPHTHALMOLOGY' | 'ORAL_AND_MAXILLOFACIAL_SURGERY' | 'ORTHOPAEDIC_SURGERY' | 'OTHER' | 'OTOLARYNGOLOGY' | 'PAIN_MEDICINE' | 'PATHOLOGY' | 'PEDIATRICS' | 'PEDIATRIC_ANESTHESIA' | 'PEDIATRIC_CRITICAL_CARE' | 'PEDIATRIC_EMERGENCY_MEDICINE' | 'PEDIATRIC_INFECTIOUS_DISEASES' | 'PHYSICAL_MEDICINE_AND_REHABILITATION' | 'PLASTIC_SURGERY' | 'PREVENTIVE_MEDICINE' | 'PRIMARY_CARE' | 'PSYCHIATRY' | 'PULMONOLOGY' | 'RADIOLOGY' | 'SURGERY' | 'THORACIC_SURGERY' | 'TRANSPLANT_SURGERY' | 'UROLOGY' | null>,
        /**
         * Status
         */
        status?: Array<string | null>,
        /**
         * Status Category
         */
        statusCategory?: string,
        /**
         * Status Kind
         *
         * * `D` - Draft
         * * `I` - Initial
         * * `T` - Transitory
         * * `F` - Final
         */
        statusKind?: Array<'D' | 'F' | 'I' | 'T'>,
    }): CancelablePromise<PaginatedProviderListList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/',
            query: {
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'profile_disposition': profileDisposition,
                'registration_status': registrationStatus,
                'search': search,
                'source_type': sourceType,
                'specialties': specialties,
                'status': status,
                'status_category': statusCategory,
                'status_kind': statusKind,
            },
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns ProviderDetail
     * @throws ApiError
     */
    public static providersRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
    }): CancelablePromise<ProviderDetail> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns ProviderList
     * @throws ApiError
     */
    public static providersContractRetrieve({
        id,
        xTz,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * The timezone of the user making the request
         */
        xTz: string,
    }): CancelablePromise<ProviderList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/contract/',
            path: {
                'id': id,
            },
            headers: {
                'x-tz': xTz,
            },
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns ProfessionalServicesAgreement
     * @throws ApiError
     */
    public static providersContractCreate({
        id,
        xTz,
        requestBody,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
        /**
         * The timezone of the user making the request
         */
        xTz: string,
        requestBody: ProfessionalServicesAgreement,
    }): CancelablePromise<ProfessionalServicesAgreement> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/providers/{id}/contract/',
            path: {
                'id': id,
            },
            headers: {
                'x-tz': xTz,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Shared provider list/retrieve (`/api/providers/`) plus the provider's own
     * contract actions.
     *
     * Chunk 1 of the provider-experience rebuild: STAFF get every provider
     * (SecurityContextMixin passes staff through); HEALTHCARE_ENTITY and PROVIDER
     * contexts fail closed on list/retrieve until the HCO chunk adds its
     * three-tier visibility (browseable / attempted connection / cleared —
     * see docs/plans/2026-09-08-staff-providers-list-design.md).
     *
     * The `contract*` actions predate this and keep their own rules: the
     * provider acting on their own row, unscoped queryset, `CanUpdateProvider`.
     * @returns binary
     * @throws ApiError
     */
    public static providersContractReviewDownloadRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this provider.
         */
        id: string,
    }): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/{id}/contract-review-download/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Provider schedule endpoints.
     *
     * GET  /api/providers/schedule/dates/?start=YYYY-MM-DD&end=YYYY-MM-DD
     * Returns ``{"allow_dates": [...], "block_dates": [...], "schedule_type": "OPEN"}``.
     * Both arrays cover the same window so a frontend mode-flip needs no
     * second fetch.
     *
     * POST /api/providers/schedule/bulk/
     * Body: ``{"is_blocked": bool, "add": [...], "remove": [...]}``.
     * Returns 204. ``is_blocked`` scopes the mutation to allow rows
     * (false) or block rows (true); the inverse kind is preserved.
     *
     * PATCH /api/providers/schedule/type/
     * Body: ``{"schedule_type": "OPEN"|"CLOSED"|"STRICT"}``.
     * Updates the caller's ``Provider.schedule_type``. Returns the new
     * value. Does not touch any schedule rows.
     *
     * The acting provider is always derived from the request's security context;
     * the body never specifies a provider_id, so a caller cannot mutate another
     * provider's schedule.
     * @returns void
     * @throws ApiError
     */
    public static providersScheduleBulkCreate({
        requestBody,
    }: {
        requestBody: ProviderScheduleBulkUpdate,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/providers/schedule/bulk/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Provider schedule endpoints.
     *
     * GET  /api/providers/schedule/dates/?start=YYYY-MM-DD&end=YYYY-MM-DD
     * Returns ``{"allow_dates": [...], "block_dates": [...], "schedule_type": "OPEN"}``.
     * Both arrays cover the same window so a frontend mode-flip needs no
     * second fetch.
     *
     * POST /api/providers/schedule/bulk/
     * Body: ``{"is_blocked": bool, "add": [...], "remove": [...]}``.
     * Returns 204. ``is_blocked`` scopes the mutation to allow rows
     * (false) or block rows (true); the inverse kind is preserved.
     *
     * PATCH /api/providers/schedule/type/
     * Body: ``{"schedule_type": "OPEN"|"CLOSED"|"STRICT"}``.
     * Updates the caller's ``Provider.schedule_type``. Returns the new
     * value. Does not touch any schedule rows.
     *
     * The acting provider is always derived from the request's security context;
     * the body never specifies a provider_id, so a caller cannot mutate another
     * provider's schedule.
     * @returns ProviderScheduleDatesResponse
     * @throws ApiError
     */
    public static providersScheduleDatesRetrieve({
        end,
        start,
    }: {
        /**
         * Window end, inclusive (YYYY-MM-DD).
         */
        end: string,
        /**
         * Window start, inclusive (YYYY-MM-DD).
         */
        start: string,
    }): CancelablePromise<ProviderScheduleDatesResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/providers/schedule/dates/',
            query: {
                'end': end,
                'start': start,
            },
        });
    }

    /**
     * Provider schedule endpoints.
     *
     * GET  /api/providers/schedule/dates/?start=YYYY-MM-DD&end=YYYY-MM-DD
     * Returns ``{"allow_dates": [...], "block_dates": [...], "schedule_type": "OPEN"}``.
     * Both arrays cover the same window so a frontend mode-flip needs no
     * second fetch.
     *
     * POST /api/providers/schedule/bulk/
     * Body: ``{"is_blocked": bool, "add": [...], "remove": [...]}``.
     * Returns 204. ``is_blocked`` scopes the mutation to allow rows
     * (false) or block rows (true); the inverse kind is preserved.
     *
     * PATCH /api/providers/schedule/type/
     * Body: ``{"schedule_type": "OPEN"|"CLOSED"|"STRICT"}``.
     * Updates the caller's ``Provider.schedule_type``. Returns the new
     * value. Does not touch any schedule rows.
     *
     * The acting provider is always derived from the request's security context;
     * the body never specifies a provider_id, so a caller cannot mutate another
     * provider's schedule.
     * @returns ScheduleTypeUpdate
     * @throws ApiError
     */
    public static providersScheduleTypePartialUpdate({
        requestBody,
    }: {
        requestBody?: PatchedScheduleTypeUpdate,
    }): CancelablePromise<ScheduleTypeUpdate> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/providers/schedule/type/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
